/**
 * Mockups Page
 *
 * Split-panel workflow page for designers to create mockups/proofs
 * from digitization results and send them for customer approval.
 */

import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  TextField,
  DropZone,
  Spinner,
  Banner,
  IndexTable,
  EmptyState,
  Divider,
  Select,
  Modal,
} from '@shopify/polaris';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useOrders, useOrderDetail, useOrderActions } from '../hooks';
import { useAIMockup, usePatchTemplates, useBillingBalance } from '../hooks/useMockups';
import {
  FILE_TYPE_LABELS,
  PRODUCT_TYPE_LABELS,
  STAGE_LABELS,
  STAGE_TONES,
  type TrackedOrder,
  type OrderFile,
  type WorkflowStage,
  type ApprovalPreviewResponse,
} from '../types';
import { getOrder, getOrders, previewApproval, sendApproval, addLineItemToDraft, setOrderProductType, setSizeSelectionProducts, getFileUploadUrl, confirmFileUpload } from '../api/orders';
import { useProductSearch, usePatchCollection } from '../hooks/useSettings';
import OrderDetailContent from '../components/OrderDetailContent';
import { getDraftOrderNameColor } from '../lib/storeUtils';

// Helper to format date
function toTimestampMs(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): number {
  if (!timestamp) return 0;
  if (typeof timestamp === 'object' && '_seconds' in timestamp) {
    return timestamp._seconds * 1000;
  }
  const ms = new Date(timestamp).getTime();
  return isNaN(ms) ? 0 : ms;
}

function formatDate(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): string {
  if (!timestamp) return 'Unknown';
  const ms = toTimestampMs(timestamp);
  if (!ms) return 'Unknown';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Helper to format file size
const BYTES_IN_KB = 1024;
const BYTES_IN_MB = 1024 * 1024;

function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes === 0 || isNaN(bytes)) return '';
  if (bytes < BYTES_IN_KB) return `${bytes} B`;
  if (bytes < BYTES_IN_MB) return `${(bytes / BYTES_IN_KB).toFixed(1)} KB`;
  return `${(bytes / BYTES_IN_MB).toFixed(1)} MB`;
}

// Helper to check if a file is an image
function isImageFile(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

// Helper to get the display URL for a file
function getFileDisplayUrl(file: OrderFile): string | null {
  if (file.externalUrl) {
    return file.externalUrl;
  }
  if (file.downloadUrl) {
    return file.downloadUrl;
  }
  return null;
}

// Reference file types to display
const REFERENCE_FILE_TYPES = ['customer_artwork', 'digitization_result', 'digitization_source'];

// Customer Portal Preview Component
function CustomerPortalPreview({
  open,
  onClose,
  order,
  proofImageUrls,
  proofNotes,
  versionHistory,
}: {
  open: boolean;
  onClose: () => void;
  order: TrackedOrder;
  proofImageUrls: string[];
  proofNotes: string;
  versionHistory?: Array<{ version: number; status: string; rejectionReason?: string }>;
}) {
  const firstName = order.customerName?.split(' ')[0] || 'there';
  const hasRevisions = versionHistory && versionHistory.length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Customer Portal Preview" size="large">
      <Modal.Section>
        <Banner tone="info">
          <p>This is a preview of what your customer will see on the approval portal.</p>
        </Banner>

        {/* Version History Banner (staff only) */}
        {hasRevisions && (
          <div style={{ marginTop: '12px' }}>
            <Banner tone="warning">
              <p style={{ fontWeight: 600, marginBottom: '4px' }}>
                Version {(versionHistory.length + 1)} — Previous feedback:
              </p>
              {versionHistory.map((v) => (
                <p key={v.version} style={{ margin: '2px 0', fontSize: '13px' }}>
                  v{v.version}: {v.status === 'rejected' && v.rejectionReason
                    ? v.rejectionReason
                    : v.status === 'approved'
                      ? 'Approved'
                      : v.status === 'expired'
                        ? 'Cancelled/expired'
                        : 'Pending'}
                </p>
              ))}
            </Banner>
          </div>
        )}

        <div
          style={{
            marginTop: '16px',
            background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ background: '#1a1a1a', padding: '20px', textAlign: 'center', borderBottom: '1px solid #333' }}>
            <div style={{ fontSize: '20px', fontWeight: 600, color: '#fff', letterSpacing: '1px' }}>
              SIX HATS SUPPLY
            </div>
          </div>

          {/* Card */}
          <div style={{ maxWidth: '600px', margin: '24px auto', padding: '0 16px 24px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
              {/* Card Header */}
              <div style={{ background: '#f8f8f8', padding: '20px', borderBottom: '1px solid #eee' }}>
                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 4px' }}>
                  Order {order.shopifyOrderName}
                </p>
                <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#1a1a1a', margin: '0 0 4px' }}>
                  Hi {firstName}!
                </h1>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                  {hasRevisions
                    ? `We've revised ${proofImageUrls.length} mockup(s) based on your feedback. Please review below.`
                    : 'Please review each mockup below.'}
                </p>
              </div>

              {/* Notes */}
              {proofNotes && (
                <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
                  <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#92400e' }}>
                    <strong>Note from our team:</strong><br />{proofNotes}
                  </div>
                </div>
              )}

              {/* Per-Mockup Review Cards */}
              <div style={{ padding: '20px' }}>
                <p style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#666', marginBottom: '16px' }}>
                  Review Mockups ({proofImageUrls.length})
                </p>
                {proofImageUrls.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {proofImageUrls.map((url, idx) => (
                      <div
                        key={idx}
                        style={{
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '16px',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                          {/* Thumbnail */}
                          <img
                            src={url}
                            alt={`Mockup ${idx + 1}`}
                            style={{
                              width: '100px',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #eee',
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '14px', fontWeight: 600, color: '#333', margin: '0 0 12px' }}>
                              Mockup {idx + 1}
                            </p>
                            {/* Action buttons (disabled preview) */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                disabled
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  border: '2px solid #d1d5db',
                                  borderRadius: '8px',
                                  background: '#fff',
                                  color: '#374151',
                                  cursor: 'not-allowed',
                                  opacity: 0.6,
                                }}
                              >
                                Approve
                              </button>
                              <button
                                disabled
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  border: '2px solid #d1d5db',
                                  borderRadius: '8px',
                                  background: '#fff',
                                  color: '#374151',
                                  cursor: 'not-allowed',
                                  opacity: 0.6,
                                }}
                              >
                                Request Changes
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#999', textAlign: 'center' }}>No mockup images available</p>
                )}
              </div>

              {/* Submit section (disabled preview) */}
              <div style={{ padding: '20px', borderTop: '1px solid #eee' }}>
                <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
                    Customer reviews each mockup individually, then submits all decisions at once.
                  </p>
                  <button
                    disabled
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '16px',
                      fontWeight: 600,
                      border: 'none',
                      borderRadius: '8px',
                      background: '#10b981',
                      color: '#fff',
                      opacity: 0.4,
                      cursor: 'not-allowed',
                    }}
                  >
                    Submit Review — Preview Only
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', padding: '0 0 24px', color: '#888', fontSize: '12px' }}>
            Questions? Contact us at proof@production.sixhats.ca
          </div>
        </div>
      </Modal.Section>
    </Modal>
  );
}

export default function Mockups() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Order list state
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(
    searchParams.get('orderId')
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Clear orderId from URL after reading it (keep URL clean)
  useEffect(() => {
    if (searchParams.has('orderId')) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Mockup form state
  const [mockupFiles, setMockupFiles] = useState<File[]>([]);
  const [proofNotes, setProofNotes] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Portal preview state
  const [showPortalPreview, setShowPortalPreview] = useState(false);

  // Quick view modal state
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  // Multi-file selection state
  const [selectedProofFileIds, setSelectedProofFileIds] = useState<string[]>([]);

  // Approval history state (for version history in portal preview)
  const [approvalHistory, setApprovalHistory] = useState<Array<{ version: number; status: string; rejectionReason?: string }>>([]);

  // Email preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<ApprovalPreviewResponse | null>(null);
  const [previewProofFileId, setPreviewProofFileId] = useState<string | null>(null);
  const [isConfirmSending, setIsConfirmSending] = useState(false);

  // Logo mapping state
  const [showLogoMapping, setShowLogoMapping] = useState(false);
  const [logoMappings, setLogoMappings] = useState<Record<string, string>>({});
  const [logoUploading, setLogoUploading] = useState(false);

  // Product type selection state
  const [productTypeLoading, setProductTypeLoading] = useState(false);
  const [editingProductType, setEditingProductType] = useState(false);
  const [patchSearchQuery, setPatchSearchQuery] = useState('');
  const patchCollectionConfig = usePatchCollection();
  const productSearch = useProductSearch(patchCollectionConfig.collection?.handle);

  // Size selection product state (draft orders)
  const [sizeSelectionProductIds, setSizeSelectionProductIdsState] = useState<string[]>([]);
  const [sizeProductTitles, setSizeProductTitles] = useState<Record<string, string>>({});
  const [sizeProductSearchQuery, setSizeProductSearchQuery] = useState('');
  const sizeProductSearch = useProductSearch();
  const [sizeProductsSaving, setSizeProductsSaving] = useState(false);

  // "Show All Orders" state
  const [showAllOrders, setShowAllOrders] = useState(false);

  // Sort state for the mockup-queue list columns (in-memory sort)
  const [sortColumnIndex, setSortColumnIndex] = useState(2); // Date column
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
  // Sort state for the "Show All Orders" list — drives a server-side sort so it
  // spans the ENTIRE selection, not just the loaded page. Default: Date, newest first.
  const [allOrdersSortIndex, setAllOrdersSortIndex] = useState(2); // Date column
  const [allOrdersSortDir, setAllOrdersSortDir] = useState<'ascending' | 'descending'>('descending');
  const [allOrders, setAllOrders] = useState<TrackedOrder[]>([]);
  const [allOrdersLoading, setAllOrdersLoading] = useState(false);
  const [allOrdersPage, setAllOrdersPage] = useState(1);
  const [allOrdersTotal, setAllOrdersTotal] = useState(0);
  const ALL_ORDERS_LIMIT = 50;

  // AI Mockup state
  const [selectedLogoFileId, setSelectedLogoFileId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [productImageLoading, setProductImageLoading] = useState(false);

  // File preview modal state
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  // Fetch orders with ORDER_RECEIVED stage (new orders needing mockups)
  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders,
  } = useOrders({ stage: 'ORDER_RECEIVED' as WorkflowStage });

  // Also fetch DIGITAL_MOCKUP orders (returned from rejection, needing revision)
  const {
    data: revisionOrdersData,
    loading: revisionOrdersLoading,
    refetch: refetchRevisionOrders,
  } = useOrders({ stage: 'DIGITAL_MOCKUP' as WorkflowStage });

  // Fetch selected order details
  const {
    data: orderDetail,
    loading: detailLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useOrderDetail(selectedOrderId || undefined);

  // Order actions
  const actions = useOrderActions(selectedOrderId || '', refetchDetail);

  // AI Mockup hook
  const aiMockup = useAIMockup(selectedOrderId || '');

  // Patch templates for AI mockup
  const patchTemplates = usePatchTemplates();

  // Billing balance
  const billing = useBillingBalance();

  // Merge ORDER_RECEIVED and DIGITAL_MOCKUP orders, filter by search, sort with revisions first
  const allMockupOrders = useMemo(() => {
    const newOrders = ordersData?.orders || [];
    const revisionOrders = revisionOrdersData?.orders || [];
    // Deduplicate by order id
    const seen = new Set<string>();
    const merged: TrackedOrder[] = [];
    for (const order of [...revisionOrders, ...newOrders]) {
      if (!seen.has(order.id)) {
        seen.add(order.id);
        merged.push(order);
      }
    }
    return merged;
  }, [ordersData, revisionOrdersData]);

  const filteredOrders = allMockupOrders.filter((order) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.shopifyOrderName.toLowerCase().includes(query) ||
      order.customerName.toLowerCase().includes(query) ||
      order.customerEmail.toLowerCase().includes(query)
    );
  }).sort((a, b) => {
    // Revision orders (DIGITAL_MOCKUP) always first
    const aIsRevision = a.currentStage === 'DIGITAL_MOCKUP' ? 0 : 1;
    const bIsRevision = b.currentStage === 'DIGITAL_MOCKUP' ? 0 : 1;
    if (aIsRevision !== bIsRevision) return aIsRevision - bIsRevision;

    let cmp = 0;
    if (sortColumnIndex === 0) {
      // Sort by order name
      cmp = a.shopifyOrderName.localeCompare(b.shopifyOrderName);
    } else if (sortColumnIndex === 1) {
      // Sort by customer name
      cmp = a.customerName.localeCompare(b.customerName);
    } else {
      // Sort by date — shopifyCreatedAt may arrive as ISO string or Firestore Timestamp
      cmp = toTimestampMs(a.shopifyCreatedAt) - toTimestampMs(b.shopifyCreatedAt);
    }
    return sortDirection === 'ascending' ? cmp : -cmp;
  });

  // Only the explicit "Show All Orders" toggle pulls the paginated, server-backed
  // list spanning EVERY stage. The search box always filters in-memory so it stays
  // scoped to mockup-relevant orders (the queue) and never surfaces shipped orders.
  // Maps the Show-All table column index to a server-side sort field.
  const ALL_ORDERS_SORT_FIELDS = ['shopifyOrderName', 'customerName', 'shopifyCreatedAt'];
  const fetchAllOrders = useCallback(async (page: number, append: boolean = false) => {
    setAllOrdersLoading(true);
    try {
      const response = await getOrders({
        excludeStage: 'ARCHIVED' as WorkflowStage,
        limit: ALL_ORDERS_LIMIT,
        page,
        sortBy: ALL_ORDERS_SORT_FIELDS[allOrdersSortIndex] || 'shopifyCreatedAt',
        sortDirection: allOrdersSortDir === 'ascending' ? 'asc' : 'desc',
      });
      if (append) {
        setAllOrders(prev => [...prev, ...response.orders]);
      } else {
        setAllOrders(response.orders);
      }
      setAllOrdersTotal(response.total);
      setAllOrdersPage(page);
    } catch {
      // Fall back silently — user can retry
    } finally {
      setAllOrdersLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrdersSortIndex, allOrdersSortDir]);

  // Fetch the server-backed list when "Show All Orders" is enabled, and re-fetch
  // page 1 whenever its sort column/direction changes (server re-sorts everything).
  useEffect(() => {
    if (showAllOrders) {
      fetchAllOrders(1, false);
    }
  }, [showAllOrders, fetchAllOrders]);

  // Load more handler — appends the next page of the all-orders list.
  const handleLoadMore = useCallback(() => {
    fetchAllOrders(allOrdersPage + 1, true);
  }, [allOrdersPage, fetchAllOrders]);

  // Display orders: the mockup queue (search-filtered in-memory) by default, or
  // the all-stages server list when "Show All Orders" is on (also search-filterable).
  const displayOrders = useMemo(() => {
    if (!showAllOrders) return filteredOrders;
    if (!searchQuery) return allOrders;
    const query = searchQuery.toLowerCase();
    return allOrders.filter((order) =>
      order.shopifyOrderName?.toLowerCase().includes(query) ||
      order.customerName?.toLowerCase().includes(query) ||
      order.customerEmail?.toLowerCase().includes(query)
    );
  }, [showAllOrders, filteredOrders, allOrders, searchQuery]);

  // "Show More" only paginates the unfiltered all-orders list.
  const hasMoreOrders = showAllOrders && !searchQuery && allOrders.length < allOrdersTotal;

  // Handle file drop (accumulates files)
  const handleFileDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setMockupFiles((prev) => [...prev, ...acceptedFiles]);
        setSendError(null);
      }
    },
    []
  );

  // Handle Send for Approval
  const handleSendForApproval = useCallback(async () => {
    if (!selectedOrderId || !orderDetail) return;

    setIsSending(true);
    setSendError(null);

    try {
      // 1. Upload mockup files if any are pending
      if (mockupFiles.length > 0) {
        for (const file of mockupFiles) {
          const uploadSuccess = await actions.uploadFile(file, 'proof');
          if (!uploadSuccess) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
        // Clear local files immediately after successful upload
        setMockupFiles([]);

        // Fetch fresh order detail directly from API (not stale React state)
        const freshDetail = await getOrder(selectedOrderId);
        const freshProofFiles = freshDetail.files.filter((f) => f.type === 'proof' || f.type === 'mockup');

        // Get rejected file IDs from fresh approval data
        const freshRejectedIds = new Set<string>();
        if (freshDetail.approvals?.length) {
          const latestApproval = freshDetail.approvals
            .filter((a) => a.mockupApprovals && a.mockupApprovals.length > 0)
            .sort((a, b) => b.version - a.version)[0];
          if (latestApproval?.mockupApprovals) {
            for (const ma of latestApproval.mockupApprovals) {
              if (ma.status === 'rejected') freshRejectedIds.add(ma.fileId);
            }
          }
        }

        // Auto-select all non-rejected proof files for the approval
        const allProofIds = freshProofFiles
          .filter((f) => !freshRejectedIds.has(f.id))
          .map((f) => f.id);
        // Fall back to all files if everything was rejected
        if (allProofIds.length === 0) {
          allProofIds.push(...freshProofFiles.map((f) => f.id));
        }
        setSelectedProofFileIds(allProofIds);

        // Also trigger React hook refetch so UI stays in sync
        refetchDetail();

        // Open logo mapping step
        setShowLogoMapping(true);
      } else {
        // No new uploads — use the already-selected proof files
        const proofFileIds = [...selectedProofFileIds];

        if (proofFileIds.length === 0) {
          throw new Error('No proof files selected. Please select or upload mockup files.');
        }

        // Open logo mapping step
        setShowLogoMapping(true);
      }
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send for approval');
    } finally {
      setIsSending(false);
    }
  }, [selectedOrderId, orderDetail, mockupFiles, proofNotes, selectedProofFileIds, actions, refetchDetail]);

  // Handle confirm send after preview — calls API directly to get real error messages
const handleConfirmSend = useCallback(async () => {
  if (!selectedOrderId) return;

  setIsConfirmSending(true);
  try {
    // Use selectedProofFileIds — these were set correctly during preview
    const proofFileIds = [...selectedProofFileIds];

    await sendApproval(selectedOrderId, {
      proofFileIds: proofFileIds.length > 0 ? proofFileIds : undefined,
      proofFileId: proofFileIds.length === 0 ? previewProofFileId || undefined : undefined,
      notes: proofNotes || undefined,
      productType: orderDetail?.order.productType,
      logoMappings: Object.keys(logoMappings).length > 0 ? logoMappings : undefined,
    });

    // Success - close modal, clear form and selection
    setShowPreview(false);
    setShowLogoMapping(false);
    setPreviewData(null);
    setPreviewProofFileId(null);
    setSelectedOrderId(null);
    setMockupFiles([]);
    setProofNotes('');
    setSelectedProofFileIds([]);
    setLogoMappings({});

    // Refresh the orders list (order will disappear as stage changed)
    await Promise.all([refetchOrders(), refetchRevisionOrders()]);
  } catch (err) {
    setShowPreview(false);
    setSendError(err instanceof Error ? err.message : 'Failed to send approval email');
  } finally {
    setIsConfirmSending(false);
  }
}, [previewProofFileId, selectedOrderId, proofNotes, selectedProofFileIds, orderDetail?.order.productType, logoMappings, refetchOrders, refetchRevisionOrders]);

  // Handle "Next: Preview Email" from logo mapping step
  const handleLogoMappingNext = useCallback(async () => {
    if (!selectedOrderId || !orderDetail) return;

    setIsSending(true);
    setSendError(null);

    try {
      if (selectedProofFileIds.length === 0) {
        throw new Error('No proof files selected.');
      }

      const preview = await previewApproval(selectedOrderId, {
        proofFileIds: selectedProofFileIds,
        notes: proofNotes || undefined,
        productType: orderDetail.order.productType,
      });

      setPreviewData(preview);
      setPreviewProofFileId(selectedProofFileIds[0]);
      setShowLogoMapping(false);
      setShowPreview(true);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to generate preview');
      setShowLogoMapping(false);
    } finally {
      setIsSending(false);
    }
  }, [selectedOrderId, orderDetail, selectedProofFileIds, proofNotes]);

  // Handle logo upload within the mapping modal
  const handleLogoUploadInMapping = useCallback(async (files: File[], forMockupFileId?: string) => {
    if (!selectedOrderId) return;

    setLogoUploading(true);
    try {
      for (const file of files) {
        const mimeType = file.type || 'application/octet-stream';
        const { uploadUrl, fileId } = await getFileUploadUrl(selectedOrderId, {
          filename: file.name,
          mimeType,
          fileType: 'customer_artwork',
        });

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': mimeType },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload: ${uploadResponse.statusText}`);
        }

        await confirmFileUpload(selectedOrderId, fileId, file.size);

        // Auto-assign this logo to the specified mockup (or all if none specified)
        if (forMockupFileId) {
          setLogoMappings((prev) => ({ ...prev, [forMockupFileId]: fileId }));
        } else {
          // "Use for all" upload — assign to all mockups
          const newMappings: Record<string, string> = {};
          for (const id of selectedProofFileIds) {
            newMappings[id] = fileId;
          }
          setLogoMappings((prev) => ({ ...prev, ...newMappings }));
        }
      }

      // Refresh order detail so new files appear in dropdowns
      refetchDetail();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  }, [selectedOrderId, selectedProofFileIds, refetchDetail]);

  // Clear form when order changes
  useEffect(() => {
    setMockupFiles([]);
    setProofNotes('');
    setSendError(null);
    setShowPreview(false);
    setShowLogoMapping(false);
    setShowPortalPreview(false);
    setQuickViewOpen(false);
    setPreviewData(null);
    setPreviewProofFileId(null);
    setSelectedProofFileIds([]);
    setLogoMappings({});
    setApprovalHistory([]);
    // Reset AI mockup state
    setSelectedLogoFileId('');
    setSelectedTemplateId('');
    setProductImageUrl(null);
    aiMockup.clearResult();
    aiMockup.clearError();
    // Reset product type selection state
    setPatchSearchQuery('');
    productSearch.clear();
    // Reset size selection state
    setSizeSelectionProductIdsState([]);
    setSizeProductTitles({});
    setSizeProductSearchQuery('');
    sizeProductSearch.clear();
  }, [selectedOrderId]);

  // Fetch product image when order detail loads
  useEffect(() => {
    async function fetchProductImage() {
      if (!orderDetail?.order.lineItems?.[0]?.shopifyProductId) {
        setProductImageUrl(null);
        return;
      }

      setProductImageLoading(true);
      try {
        // Use the order's first line item product ID to fetch from Shopify
        const productId = orderDetail.order.lineItems[0].shopifyProductId;
        const response = await fetch(`/api/orders/${selectedOrderId}/product-image/${productId}`);
        if (response.ok) {
          const data = await response.json();
          setProductImageUrl(data.imageUrl || null);
        } else {
          setProductImageUrl(null);
        }
      } catch (error) {
        console.error('Failed to fetch product image:', error);
        setProductImageUrl(null);
      } finally {
        setProductImageLoading(false);
      }
    }

    if (orderDetail && selectedOrderId) {
      fetchProductImage();
    }
  }, [orderDetail, selectedOrderId]);

  // Load size selection product IDs from order detail and build title map from line items
  useEffect(() => {
    if (orderDetail?.order) {
      // Build title map from line items
      const titles: Record<string, string> = {};
      for (const item of orderDetail.order.lineItems) {
        if (item.shopifyProductId) {
          titles[item.shopifyProductId] = item.productTitle;
        }
      }
      setSizeProductTitles((prev) => ({ ...prev, ...titles }));

      if (orderDetail.order.sizeSelectionProductIds) {
        setSizeSelectionProductIdsState(orderDetail.order.sizeSelectionProductIds);
      }
    }
  }, [orderDetail?.order]);

  // Helper to save size selection products
  const saveSizeSelectionProducts = useCallback(async (productIds: string[]) => {
    if (!selectedOrderId) return;
    setSizeProductsSaving(true);
    try {
      await setSizeSelectionProducts(selectedOrderId, productIds);
      setSizeSelectionProductIdsState(productIds);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to save size selection products');
    } finally {
      setSizeProductsSaving(false);
    }
  }, [selectedOrderId]);

  // Auto-load patch products when selecting a draft order that has type 'patch' or 'both'
  useEffect(() => {
    if (
      orderDetail?.order.isDraft &&
      (orderDetail.order.productType === 'patch' || orderDetail.order.productType === 'both') &&
      patchCollectionConfig.collection?.handle
    ) {
      productSearch.search('');
    }
  }, [selectedOrderId, orderDetail?.order.productType, patchCollectionConfig.collection?.handle]);

  // Get reference files (customer artwork and digitization results)
  const referenceFiles = orderDetail?.files.filter((f) =>
    REFERENCE_FILE_TYPES.includes(f.type)
  ) || [];

  // Get customer artwork files for AI mockup selection
  const customerArtworkFiles = orderDetail?.files.filter((f) => f.type === 'customer_artwork') || [];
  const customerArtworkOptions = [
    { label: 'Select a logo file...', value: '' },
    ...customerArtworkFiles.map((file) => ({
      label: file.originalFilename,
      value: file.id,
    })),
  ];

  // Get the selected logo file for preview
  const selectedLogoFile = customerArtworkFiles.find((f) => f.id === selectedLogoFileId);
  const selectedLogoPreviewUrl = selectedLogoFile ? getFileDisplayUrl(selectedLogoFile) : null;

  // Get template options for AI mockup selection
  const templateOptions = [
    { label: 'Select a patch template...', value: '' },
    ...patchTemplates.templates.map((template) => ({
      label: template.name,
      value: template.id,
    })),
  ];
  const selectedTemplate = patchTemplates.templates.find((t) => t.id === selectedTemplateId);

  // Get existing proof files (including AI-generated mockups)
  const existingProofFiles = orderDetail?.files.filter((f) => f.type === 'proof' || f.type === 'mockup') || [];

  // Get rejected file IDs from the latest approval so we can exclude them from auto-selection
  const rejectedFileIds = useMemo(() => {
    const ids = new Set<string>();
    if (!orderDetail?.approvals?.length) return ids;
    const latestApproval = orderDetail.approvals
      .filter((a) => a.mockupApprovals && a.mockupApprovals.length > 0)
      .sort((a, b) => b.version - a.version)[0];
    if (latestApproval?.mockupApprovals) {
      for (const ma of latestApproval.mockupApprovals) {
        if (ma.status === 'rejected') ids.add(ma.fileId);
      }
    }
    return ids;
  }, [orderDetail]);

  // Auto-select new proof files when they appear (excluding rejected mockups)
  useEffect(() => {
    if (existingProofFiles.length > 0 && selectedProofFileIds.length === 0) {
      // Auto-select non-rejected proof files
      const nonRejectedIds = existingProofFiles
        .filter((f) => !rejectedFileIds.has(f.id))
        .map((f) => f.id);
      if (nonRejectedIds.length > 0) {
        setSelectedProofFileIds(nonRejectedIds);
      } else {
        // All files are rejected — select the latest one as fallback
        setSelectedProofFileIds([existingProofFiles[existingProofFiles.length - 1].id]);
      }
    } else if (existingProofFiles.length > 0) {
      // Check if a new file appeared that isn't selected yet — auto-add it (only non-rejected)
      const currentIds = new Set(selectedProofFileIds);
      const allProofIds = existingProofFiles.map((f) => f.id);
      const newIds = allProofIds.filter((id) => !currentIds.has(id) && !rejectedFileIds.has(id));
      if (newIds.length > 0) {
        setSelectedProofFileIds((prev) => [...prev, ...newIds]);
      }
    }
  }, [existingProofFiles.length]);

  // Fetch approval history for version history display
  useEffect(() => {
    if (!selectedOrderId) return;
    const fetchHistory = async () => {
      try {
        const response = await fetch(`/api/approvals/order/${selectedOrderId}`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setApprovalHistory(
              data.map((a: { version: number; status: string; rejectionReason?: string }) => ({
                version: a.version,
                status: a.status,
                rejectionReason: a.rejectionReason,
              }))
            );
          }
        }
      } catch {
        // Not critical — just don't show history
      }
    };
    fetchHistory();
  }, [selectedOrderId]);

  // Calculate if we can send (have mockup file OR selected proof files, and product type is set)
  const isUndetermined = orderDetail?.order.productType === 'undetermined';
  const isDraft = orderDetail?.order.isDraft === true;
  const hasSizeProducts = !isDraft || sizeSelectionProductIds.length > 0;
  const canSend = (mockupFiles.length > 0 || selectedProofFileIds.length > 0) && !isUndetermined && hasSizeProducts;

  // Derive proof image URLs for portal preview (multi-image)
  const portalPreviewImageUrls = useMemo(() => {
    const urls: string[] = [];
    for (const file of mockupFiles) {
      if (file.type.startsWith('image/')) {
        urls.push(URL.createObjectURL(file));
      }
    }
    for (const fileId of selectedProofFileIds) {
      const file = existingProofFiles.find((f) => f.id === fileId);
      if (file) {
        const url = getFileDisplayUrl(file);
        if (url) urls.push(url);
      }
    }
    return urls;
  }, [mockupFiles, selectedProofFileIds, existingProofFiles]);

  // Compute rejected mockups from the latest approval (for Customer Requested Changes card)
  const rejectedMockups = useMemo(() => {
    if (!orderDetail?.approvals?.length) return [];
    // Get the latest approval that has per-mockup data
    const latestApproval = orderDetail.approvals
      .filter((a) => a.mockupApprovals && a.mockupApprovals.length > 0)
      .sort((a, b) => b.version - a.version)[0];
    if (!latestApproval?.mockupApprovals) return [];

    return latestApproval.mockupApprovals
      .filter((ma) => ma.status === 'rejected')
      .map((ma) => {
        const file = orderDetail.files.find((f) => f.id === ma.fileId);
        return {
          fileId: ma.fileId,
          rejectionReason: ma.rejectionReason || 'No reason provided',
          filename: file?.originalFilename || 'Unknown file',
          imageUrl: file ? getFileDisplayUrl(file) : null,
          isImage: file ? isImageFile(file.mimeType) : false,
        };
      });
  }, [orderDetail]);

  return (
    <Page title="Mockups">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '400px 1fr',
          gap: '20px',
          height: 'calc(100vh - 140px)',
          minHeight: '600px',
        }}
      >
        {/* Left Panel - Order List */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {searchQuery
                    ? 'Search Results'
                    : showAllOrders
                      ? 'All Orders'
                      : 'Orders Ready for Mockup'}
                </Text>
                <Button
                  variant="plain"
                  onClick={() => setShowAllOrders(!showAllOrders)}
                >
                  {showAllOrders ? 'Show Queue Only' : 'Show All Orders'}
                </Button>
              </InlineStack>
              <TextField
                label=""
                labelHidden
                placeholder="Search by name, order #, or email..."
                value={searchQuery}
                onChange={setSearchQuery}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setSearchQuery('')}
              />
            </BlockStack>
          </Card>

          <div style={{ flex: 1, overflow: 'auto', marginTop: '16px' }}>
            <Card padding="0">
              {(showAllOrders ? allOrdersLoading && allOrders.length === 0 : ordersLoading || revisionOrdersLoading) ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              ) : (!showAllOrders && ordersError) ? (
                <div style={{ padding: '20px' }}>
                  <Banner tone="critical">
                    <p>{ordersError}</p>
                  </Banner>
                </div>
              ) : displayOrders.length === 0 ? (
                <EmptyState
                  heading={searchQuery ? 'No matching orders' : showAllOrders ? 'No orders found' : 'No orders ready for mockup'}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>{searchQuery ? 'Try a different search term' : showAllOrders ? 'No active orders' : 'New orders will appear here'}</p>
                </EmptyState>
              ) : (
                <>
                  <IndexTable
                    itemCount={displayOrders.length}
                    headings={
                      showAllOrders
                        ? [{ title: 'Order' }, { title: 'Customer' }, { title: 'Date' }, { title: 'Stage' }]
                        : [{ title: 'Order' }, { title: 'Customer' }, { title: 'Date' }]
                    }
                    selectable={false}
                    sortable={showAllOrders ? [true, true, true, false] : [true, true, true]}
                    sortDirection={showAllOrders ? allOrdersSortDir : sortDirection}
                    sortColumnIndex={showAllOrders ? allOrdersSortIndex : sortColumnIndex}
                    onSort={(index, direction) => {
                      if (showAllOrders) {
                        // Drives a server-side re-sort across the entire selection.
                        setAllOrdersSortIndex(index);
                        setAllOrdersSortDir(direction);
                      } else {
                        setSortColumnIndex(index);
                        setSortDirection(direction);
                      }
                    }}
                  >
                    {displayOrders.map((order: TrackedOrder, index: number) => (
                      <IndexTable.Row
                        key={order.id}
                        id={order.id}
                        position={index}
                        onClick={() => { setSelectedOrderId(order.id); setEditingProductType(false); }}
                        selected={selectedOrderId === order.id}
                      >
                        <IndexTable.Cell>
                          <InlineStack gap="100" blockAlign="center" wrap={false}>
                            <span style={{ color: order.isDraft ? getDraftOrderNameColor(order.shopDomain) : undefined }}>
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {order.shopifyOrderName}
                              </Text>
                            </span>
                            {!showAllOrders && order.currentStage === 'DIGITAL_MOCKUP' && (
                              <Badge tone="critical" size="small">Revision</Badge>
                            )}
                          </InlineStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {/* Truncate long customer names so they never widen the
                              narrow list pane and push the Date column off-screen. */}
                          <div
                            title={order.customerName}
                            style={{ maxWidth: showAllOrders ? '110px' : '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            <Text as="span" variant="bodySm">
                              {order.customerName}
                            </Text>
                          </div>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {formatDate(order.shopifyCreatedAt)}
                          </Text>
                        </IndexTable.Cell>
                        {showAllOrders && (
                          <IndexTable.Cell>
                            <Badge
                              tone={STAGE_TONES[order.currentStage] as 'info' | 'success' | 'warning' | 'critical' | 'attention' | undefined}
                              size="small"
                            >
                              {STAGE_LABELS[order.currentStage] || order.currentStage}
                            </Badge>
                          </IndexTable.Cell>
                        )}
                      </IndexTable.Row>
                    ))}
                  </IndexTable>
                  {hasMoreOrders && (
                    <div style={{ padding: '12px', textAlign: 'center' }}>
                      <Button
                        variant="plain"
                        onClick={handleLoadMore}
                        loading={allOrdersLoading}
                      >
                        {`Show More (${allOrdersTotal - displayOrders.length} more)`}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>

          <div style={{ marginTop: '16px' }}>
            <Text as="p" variant="bodySm" tone="subdued">
              {showAllOrders
                ? (searchQuery
                    ? `${displayOrders.length} matching order${displayOrders.length !== 1 ? 's' : ''}`
                    : `${displayOrders.length} of ${allOrdersTotal} order${allOrdersTotal !== 1 ? 's' : ''}`)
                : `${displayOrders.length} order${displayOrders.length !== 1 ? 's' : ''} ready`
              }
            </Text>
          </div>
        </div>

        {/* Right Panel - Workspace */}
        <div
          style={{
            height: '100%',
            overflow: 'auto',
          }}
        >
          {!selectedOrderId ? (
            <Card>
              <EmptyState
                heading="Select an order"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Choose an order from the list to create a mockup</p>
              </EmptyState>
            </Card>
          ) : detailLoading ? (
            <Card>
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          ) : detailError || !orderDetail ? (
            <Card>
              <Banner tone="critical">
                <p>{detailError || 'Failed to load order details'}</p>
              </Banner>
            </Card>
          ) : (
            <BlockStack gap="400">
              {/* Send Error Banner */}
              {sendError && (
                <Banner tone="critical" onDismiss={() => setSendError(null)}>
                  <p>{sendError}</p>
                </Banner>
              )}

              {/* Order Info Card */}
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <InlineStack gap="200" blockAlign="center">
                      <span style={{ color: orderDetail.order.isDraft ? getDraftOrderNameColor(orderDetail.order.shopDomain) : undefined }}>
                        <Text as="h2" variant="headingMd">
                          Order {orderDetail.order.shopifyOrderName}
                        </Text>
                      </span>
                      <Button
                        size="slim"
                        onClick={() => setQuickViewOpen(true)}
                      >
                        View Details
                      </Button>
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center">
                      <Badge tone={orderDetail.order.productType === 'undetermined' ? 'attention' : 'success'}>
                        {PRODUCT_TYPE_LABELS[orderDetail.order.productType]}
                      </Badge>
                      {!isUndetermined && !editingProductType && (
                        <Button
                          size="micro"
                          variant="plain"
                          onClick={() => setEditingProductType(true)}
                        >
                          Edit
                        </Button>
                      )}
                      <Badge tone={STAGE_TONES[orderDetail.order.currentStage] || 'info'}>
                        {STAGE_LABELS[orderDetail.order.currentStage] || orderDetail.order.currentStage}
                      </Badge>
                    </InlineStack>
                  </InlineStack>

                  <Divider />

                  {/* Customer Info */}
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Customer
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {orderDetail.order.customerName}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {orderDetail.order.customerEmail}
                    </Text>
                    {orderDetail.order.customerPhone && (
                      <Text as="span" variant="bodySm" tone="subdued">
                        {orderDetail.order.customerPhone}
                      </Text>
                    )}
                  </BlockStack>

                  {/* Order Notes */}
                  {orderDetail.order.shopifyNote && (
                    <>
                      <Divider />
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Order Notes
                        </Text>
                        <div
                          style={{
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            padding: '12px',
                            borderLeft: '4px solid #f59e0b',
                            whiteSpace: 'pre-wrap',
                            fontSize: '13px',
                            lineHeight: '1.4',
                          }}
                        >
                          {orderDetail.order.shopifyNote.split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
                            /^https?:\/\//.test(part) ? (
                              <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#2c6ecb', wordBreak: 'break-all' }}>
                                {part}
                              </a>
                            ) : (
                              part
                            )
                          )}
                        </div>
                      </BlockStack>
                    </>
                  )}
                </BlockStack>
              </Card>

              {/* Size Selection Products Card (draft orders only) */}
              {isDraft && orderDetail && (
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        Size Selection Products
                      </Text>
                      <InlineStack gap="200">
                        {sizeProductsSaving && <Spinner size="small" />}
                        {sizeSelectionProductIds.length > 0 ? (
                          <Badge tone="success">{`${sizeSelectionProductIds.length} selected`}</Badge>
                        ) : (
                          <Badge tone="attention">Required</Badge>
                        )}
                      </InlineStack>
                    </InlineStack>
                    <Divider />

                    <Text as="p" variant="bodySm" tone="subdued">
                      Choose which products the customer can select sizes from after approval.
                    </Text>

                    {/* Quick-picks from line items */}
                    {(() => {
                      // De-duplicate line items by shopifyProductId
                      const seenProducts = new Map<string, { productId: string; title: string }>();
                      for (const item of orderDetail.order.lineItems) {
                        if (item.shopifyProductId && !seenProducts.has(item.shopifyProductId)) {
                          seenProducts.set(item.shopifyProductId, {
                            productId: item.shopifyProductId,
                            title: item.productTitle,
                          });
                        }
                      }
                      const uniqueLineItemProducts = Array.from(seenProducts.values());

                      if (uniqueLineItemProducts.length === 0) return null;

                      return (
                        <BlockStack gap="200">
                          <Text as="span" variant="bodySm" fontWeight="semibold">
                            From Order Line Items
                          </Text>
                          <InlineStack gap="200" wrap>
                            {uniqueLineItemProducts.map((product) => {
                              const isSelected = sizeSelectionProductIds.includes(product.productId);
                              return (
                                <Button
                                  key={product.productId}
                                  size="slim"
                                  variant={isSelected ? 'primary' : 'secondary'}
                                  onClick={() => {
                                    const newIds = isSelected
                                      ? sizeSelectionProductIds.filter((id) => id !== product.productId)
                                      : [...sizeSelectionProductIds, product.productId];
                                    saveSizeSelectionProducts(newIds);
                                  }}
                                  disabled={sizeProductsSaving}
                                >
                                  {isSelected ? '✓ ' : ''}{product.title}
                                </Button>
                              );
                            })}
                          </InlineStack>
                        </BlockStack>
                      );
                    })()}

                    {/* Search for additional products */}
                    <BlockStack gap="200">
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        Search Additional Products
                      </Text>
                      <TextField
                        label=""
                        labelHidden
                        placeholder="Search products to add..."
                        value={sizeProductSearchQuery}
                        onChange={(value) => {
                          setSizeProductSearchQuery(value);
                          if (value.trim().length >= 2) {
                            sizeProductSearch.search(value);
                          } else if (value.trim().length === 0) {
                            sizeProductSearch.clear();
                          }
                        }}
                        autoComplete="off"
                        clearButton
                        onClearButtonClick={() => {
                          setSizeProductSearchQuery('');
                          sizeProductSearch.clear();
                        }}
                      />
                      {sizeProductSearch.loading && (
                        <InlineStack gap="200" blockAlign="center">
                          <Spinner size="small" />
                          <Text as="span" variant="bodySm" tone="subdued">
                            Searching...
                          </Text>
                        </InlineStack>
                      )}
                      {sizeProductSearch.error && (
                        <Banner tone="critical">
                          <p>{sizeProductSearch.error}</p>
                        </Banner>
                      )}
                      {sizeProductSearch.results.length > 0 && (
                        <BlockStack gap="200">
                          {sizeProductSearch.results
                            .filter((product) => {
                              // Extract numeric ID from GID
                              const numericId = product.id.replace(/^gid:\/\/shopify\/Product\//, '');
                              return !sizeSelectionProductIds.includes(numericId);
                            })
                            .map((product) => {
                              const numericId = product.id.replace(/^gid:\/\/shopify\/Product\//, '');
                              return (
                                <div
                                  key={product.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '10px 12px',
                                    border: '1px solid #e1e3e5',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.background = '#f6f6f7';
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.background = '';
                                  }}
                                  onClick={() => {
                                    setSizeProductTitles((prev) => ({ ...prev, [numericId]: product.title }));
                                    const newIds = [...sizeSelectionProductIds, numericId];
                                    saveSizeSelectionProducts(newIds);
                                    setSizeProductSearchQuery('');
                                    sizeProductSearch.clear();
                                  }}
                                >
                                  {product.featuredImage?.url && (
                                    <img
                                      src={product.featuredImage.url}
                                      alt={product.title}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        border: '1px solid #e1e3e5',
                                      }}
                                    />
                                  )}
                                  <BlockStack gap="050">
                                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                                      {product.title}
                                    </Text>
                                    {product.vendor && (
                                      <Text as="span" variant="bodySm" tone="subdued">
                                        {product.vendor}
                                      </Text>
                                    )}
                                  </BlockStack>
                                </div>
                              );
                            })}
                        </BlockStack>
                      )}
                    </BlockStack>

                    {/* Selected products list */}
                    {sizeSelectionProductIds.length > 0 && (
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" fontWeight="semibold">
                          Selected Products
                        </Text>
                        {sizeSelectionProductIds.map((productId) => {
                          const title = sizeProductTitles[productId] || `Product ${productId}`;
                          return (
                            <InlineStack key={productId} align="space-between" blockAlign="center">
                              <Text as="span" variant="bodySm">
                                {title}
                              </Text>
                              <Button
                                size="slim"
                                variant="plain"
                                tone="critical"
                                onClick={() => {
                                  const newIds = sizeSelectionProductIds.filter((id) => id !== productId);
                                  saveSizeSelectionProducts(newIds);
                                }}
                                disabled={sizeProductsSaving}
                              >
                                Remove
                              </Button>
                            </InlineStack>
                          );
                        })}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              )}

              {/* Customer Requested Changes Card */}
              {rejectedMockups.length > 0 && (
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        Customer Requested Changes
                      </Text>
                      <Badge tone="critical">
                        {`${rejectedMockups.length} mockup${rejectedMockups.length !== 1 ? 's' : ''} rejected`}
                      </Badge>
                    </InlineStack>
                    <Divider />
                    {rejectedMockups.map((mockup) => (
                      <div
                        key={mockup.fileId}
                        style={{
                          display: 'flex',
                          gap: '16px',
                          padding: '12px',
                          backgroundColor: '#fef2f2',
                          borderRadius: '8px',
                          borderLeft: '4px solid #dc2626',
                        }}
                      >
                        {/* Mockup Image */}
                        {mockup.isImage && mockup.imageUrl && (
                          <div
                            style={{
                              flexShrink: 0,
                              width: '120px',
                              height: '120px',
                              backgroundColor: '#fff',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              border: '1px solid #e5e5e5',
                            }}
                          >
                            <img
                              src={mockup.imageUrl}
                              alt={mockup.filename}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          </div>
                        )}
                        {/* Rejection Details */}
                        <BlockStack gap="200">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {mockup.filename}
                          </Text>
                          <Text as="p" variant="bodyMd">
                            {mockup.rejectionReason}
                          </Text>
                        </BlockStack>
                      </div>
                    ))}
                  </BlockStack>
                </Card>
              )}

              {/* Product Type Selection Card */}
              {(isUndetermined || editingProductType) && (
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        {isUndetermined ? 'Set Product Type' : 'Change Product Type'}
                      </Text>
                      {isUndetermined ? (
                        <Badge tone="attention">Required</Badge>
                      ) : (
                        <Button size="slim" variant="plain" onClick={() => setEditingProductType(false)}>
                          Cancel
                        </Button>
                      )}
                    </InlineStack>
                    <Divider />
                    {isUndetermined && (
                      <Banner tone="warning">
                        <p>This order's product type has not been determined. Set the type before sending for approval.</p>
                      </Banner>
                    )}
                    <InlineStack gap="300">
                      <Button
                        variant={orderDetail.order.productType === 'embroidery' ? 'primary' : 'secondary'}
                        loading={productTypeLoading}
                        onClick={async () => {
                          if (!selectedOrderId) return;
                          setProductTypeLoading(true);
                          try {
                            await setOrderProductType(selectedOrderId, 'embroidery');
                            await refetchDetail();
                            setEditingProductType(false);
                          } catch (err) {
                            setSendError(err instanceof Error ? err.message : 'Failed to set product type');
                          } finally {
                            setProductTypeLoading(false);
                          }
                        }}
                      >
                        Embroidery
                      </Button>
                      <Button
                        variant={orderDetail.order.productType === 'patch' ? 'primary' : 'secondary'}
                        loading={productTypeLoading}
                        onClick={async () => {
                          if (!selectedOrderId) return;
                          setProductTypeLoading(true);
                          try {
                            await setOrderProductType(selectedOrderId, 'patch');
                            await refetchDetail();
                            setEditingProductType(false);
                            // Auto-load products from the configured collection for drafts
                            if (orderDetail.order.isDraft) {
                              setPatchSearchQuery('');
                              if (patchCollectionConfig.collection?.handle) {
                                productSearch.search('');
                              } else {
                                productSearch.clear();
                              }
                            } else {
                              productSearch.clear();
                            }
                          } catch (err) {
                            setSendError(err instanceof Error ? err.message : 'Failed to set product type');
                          } finally {
                            setProductTypeLoading(false);
                          }
                        }}
                      >
                        Patch
                      </Button>
                      <Button
                        variant={orderDetail.order.productType === 'both' ? 'primary' : 'secondary'}
                        loading={productTypeLoading}
                        onClick={async () => {
                          if (!selectedOrderId) return;
                          setProductTypeLoading(true);
                          try {
                            await setOrderProductType(selectedOrderId, 'both');
                            await refetchDetail();
                            setEditingProductType(false);
                            // Auto-load patch products for 'both' type drafts
                            if (orderDetail.order.isDraft) {
                              setPatchSearchQuery('');
                              if (patchCollectionConfig.collection?.handle) {
                                productSearch.search('');
                              } else {
                                productSearch.clear();
                              }
                            } else {
                              productSearch.clear();
                            }
                          } catch (err) {
                            setSendError(err instanceof Error ? err.message : 'Failed to set product type');
                          } finally {
                            setProductTypeLoading(false);
                          }
                        }}
                      >
                        Both
                      </Button>
                    </InlineStack>

                  </BlockStack>
                </Card>
              )}

              {/* Patch Product Selector Card (draft orders with type patch or both, always visible) */}
              {orderDetail.order.isDraft && (orderDetail.order.productType === 'patch' || orderDetail.order.productType === 'both') && (
                <Card>
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">
                      Add Patch Product
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {patchCollectionConfig.collection
                        ? `Select a product from "${patchCollectionConfig.collection.title}":`
                        : 'Search for a patch product to add to this draft order:'}
                    </Text>
                    <TextField
                      label=""
                      labelHidden
                      placeholder="Search patch products..."
                      value={patchSearchQuery}
                      onChange={(value) => {
                        setPatchSearchQuery(value);
                        if (patchCollectionConfig.collection?.handle) {
                          productSearch.search(value);
                        } else if (value.trim().length >= 2) {
                          productSearch.search(value);
                        } else if (value.trim().length === 0) {
                          productSearch.clear();
                        }
                      }}
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => {
                        setPatchSearchQuery('');
                        if (patchCollectionConfig.collection?.handle) {
                          productSearch.search('');
                        } else {
                          productSearch.clear();
                        }
                      }}
                    />
                    {productSearch.loading && (
                      <InlineStack gap="200" blockAlign="center">
                        <Spinner size="small" />
                        <Text as="span" variant="bodySm" tone="subdued">
                          Searching...
                        </Text>
                      </InlineStack>
                    )}
                    {productSearch.error && (
                      <Banner tone="critical">
                        <p>{productSearch.error}</p>
                      </Banner>
                    )}
                    {productSearch.results.length > 0 && (
                      <BlockStack gap="200">
                        {productSearch.results.map((product) => (
                          <div
                            key={product.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '10px 12px',
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLDivElement).style.background = '#f6f6f7';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLDivElement).style.background = '';
                            }}
                            onClick={async () => {
                              if (!selectedOrderId) return;
                              const firstVariant = product.variants?.edges?.[0]?.node;
                              const variantId = firstVariant
                                ? firstVariant.id.replace(/^gid:\/\/shopify\/ProductVariant\//, '')
                                : product.id;
                              setProductTypeLoading(true);
                              try {
                                await addLineItemToDraft(selectedOrderId, {
                                  variantId,
                                  quantity: 1,
                                });
                                await refetchDetail();
                                setPatchSearchQuery('');
                                productSearch.clear();
                              } catch (err) {
                                setSendError(err instanceof Error ? err.message : 'Failed to add patch product');
                              } finally {
                                setProductTypeLoading(false);
                              }
                            }}
                          >
                            {product.featuredImage?.url && (
                              <img
                                src={product.featuredImage.url}
                                alt={product.title}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #e1e3e5',
                                }}
                              />
                            )}
                            <BlockStack gap="050">
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {product.title}
                              </Text>
                              {product.vendor && (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {product.vendor}
                                </Text>
                              )}
                            </BlockStack>
                          </div>
                        ))}
                        {productSearch.hasMore && (
                          <Button
                            variant="plain"
                            loading={productSearch.loading}
                            onClick={() => productSearch.loadMore()}
                          >
                            Load more results
                          </Button>
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              )}

              {/* Line Items Breakdown Card */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Order Details
                  </Text>
                  <Divider />

                  {orderDetail.order.lineItems.map((item, idx) => (
                    <BlockStack key={idx} gap="300">
                      {idx > 0 && <Divider />}

                      {/* Product Header */}
                      <InlineStack align="space-between" blockAlign="start">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="bold">
                            {item.productTitle}
                          </Text>
                          {item.variantTitle && (
                            <Text as="span" variant="bodySm" tone="subdued">
                              Variant: {item.variantTitle}
                            </Text>
                          )}
                        </BlockStack>
                        <InlineStack gap="200">
                          {item.sku && (
                            <Badge tone="info">{`SKU: ${item.sku}`}</Badge>
                          )}
                          <Badge>{`Qty: ${item.quantity}`}</Badge>
                        </InlineStack>
                      </InlineStack>

                      {/* Line Item Properties (Customizations) */}
                      {item.properties.length > 0 && (
                        <div
                          style={{
                            backgroundColor: '#f6f6f7',
                            borderRadius: '8px',
                            padding: '12px',
                          }}
                        >
                          <BlockStack gap="200">
                            <Text as="span" variant="bodySm" fontWeight="semibold">
                              Customization Details
                            </Text>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: '8px',
                              }}
                            >
                              {item.properties
                                .filter((prop) => !prop.name.startsWith('_')) // Hide internal properties
                                .map((prop, propIdx) => (
                                  <BlockStack key={propIdx} gap="050">
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      {prop.name}
                                    </Text>
                                    <Text as="span" variant="bodySm" fontWeight="medium">
                                      {prop.value.startsWith('http') ? (
                                        <a
                                          href={prop.value}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#2c6ecb', textDecoration: 'none' }}
                                        >
                                          View Link
                                        </a>
                                      ) : (
                                        prop.value
                                      )}
                                    </Text>
                                  </BlockStack>
                                ))}
                            </div>
                          </BlockStack>
                        </div>
                      )}
                    </BlockStack>
                  ))}
                </BlockStack>
              </Card>

              {/* Reference Files Card */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Reference Files
                  </Text>
                  <Divider />

                  {referenceFiles.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No reference files available
                    </Text>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      {referenceFiles.map((file) => {
                        const displayUrl = getFileDisplayUrl(file);
                        const canDisplayImage = isImageFile(file.mimeType) && displayUrl;

                        return (
                          <div
                            key={file.id}
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              overflow: 'hidden',
                            }}
                          >
                            {/* Thumbnail */}
                            <div
                              onClick={() => {
                                if (canDisplayImage) {
                                  setPreviewImage({ url: displayUrl!, filename: file.originalFilename });
                                }
                              }}
                              style={{
                                width: '100%',
                                height: '150px',
                                backgroundColor: '#f4f6f8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: canDisplayImage ? 'pointer' : 'default',
                              }}
                            >
                              {canDisplayImage ? (
                                <img
                                  src={displayUrl}
                                  alt={file.originalFilename}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                  }}
                                />
                              ) : (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {file.mimeType.split('/')[1]?.toUpperCase() || 'FILE'}
                                </Text>
                              )}
                            </div>
                            {/* File info */}
                            <div style={{ padding: '12px' }}>
                              <BlockStack gap="100">
                                <span
                                  onClick={() => {
                                    if (file.externalUrl) {
                                      window.open(file.externalUrl, '_blank');
                                    } else {
                                      actions.downloadFile(file.id, file.originalFilename);
                                    }
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <Text as="span" variant="bodySm" fontWeight="semibold">
                                    {file.originalFilename.length > 25
                                      ? file.originalFilename.substring(0, 22) + '...'
                                      : file.originalFilename}
                                  </Text>
                                </span>
                                <InlineStack gap="100">
                                  <Badge tone="info" size="small">
                                    {FILE_TYPE_LABELS[file.type]}
                                  </Badge>
                                  {formatFileSize(file.sizeBytes) && (
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      {formatFileSize(file.sizeBytes)}
                                    </Text>
                                  )}
                                </InlineStack>
                                <Button
                                  variant="plain"
                                  size="slim"
                                  onClick={() => {
                                    if (file.externalUrl) {
                                      window.open(file.externalUrl, '_blank');
                                    } else {
                                      actions.downloadFile(file.id, file.originalFilename);
                                    }
                                  }}
                                >
                                  Download
                                </Button>
                              </BlockStack>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </BlockStack>
              </Card>

              {/* AI Mockup Generation Card */}
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <Text as="h2" variant="headingMd">
                      Generate with AI
                    </Text>
                    <InlineStack gap="200">
                      {billing.balance && (
                        <Badge tone={billing.balance.totalImageCapacity > 0 ? 'success' : 'critical'}>
                          {`${billing.balance.totalImageCapacity} image${billing.balance.totalImageCapacity !== 1 ? 's' : ''} available`}
                        </Badge>
                      )}
                      <Badge tone="info">Gemini AI</Badge>
                    </InlineStack>
                  </InlineStack>
                  <Divider />

                  {/* Billing warning */}
                  {billing.balance && billing.balance.totalImageCapacity === 0 && (
                    <Banner tone="warning">
                      <p>No image credits available. Please add more credits to generate mockups.</p>
                    </Banner>
                  )}

                  {/* Logo Selection */}
                  <BlockStack gap="200">
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      Customer Logo
                    </Text>
                    {customerArtworkFiles.length === 0 ? (
                      <Banner tone="warning">
                        <p>No customer artwork files available</p>
                      </Banner>
                    ) : (
                      <>
                        <Select
                          label=""
                          labelHidden
                          options={customerArtworkOptions}
                          value={selectedLogoFileId}
                          onChange={setSelectedLogoFileId}
                        />
                        {selectedLogoPreviewUrl && (
                          <div
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              padding: '8px',
                              backgroundColor: '#f4f6f8',
                            }}
                          >
                            <img
                              src={selectedLogoPreviewUrl}
                              alt="Selected logo preview"
                              style={{ maxHeight: '80px', objectFit: 'contain' }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>

                  {/* Patch Template Selection */}
                  <BlockStack gap="200">
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      Patch Template
                    </Text>
                    {patchTemplates.loading ? (
                      <InlineStack gap="200" blockAlign="center">
                        <Spinner size="small" />
                        <Text as="span" variant="bodySm" tone="subdued">
                          Loading templates...
                        </Text>
                      </InlineStack>
                    ) : patchTemplates.templates.length === 0 ? (
                      <Banner tone="warning">
                        <p>No patch templates available. Add templates in Settings.</p>
                      </Banner>
                    ) : (
                      <>
                        <Select
                          label=""
                          labelHidden
                          options={templateOptions}
                          value={selectedTemplateId}
                          onChange={setSelectedTemplateId}
                        />
                        {selectedTemplate?.previewUrl && (
                          <div
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              padding: '8px',
                              backgroundColor: '#f4f6f8',
                            }}
                          >
                            <img
                              src={selectedTemplate.previewUrl}
                              alt={selectedTemplate.name}
                              style={{ maxHeight: '80px', objectFit: 'contain' }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>

                  {/* Product Image Preview */}
                  <BlockStack gap="200">
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      Product Image
                    </Text>
                    {productImageLoading ? (
                      <InlineStack gap="200" blockAlign="center">
                        <Spinner size="small" />
                        <Text as="span" variant="bodySm" tone="subdued">
                          Loading product image...
                        </Text>
                      </InlineStack>
                    ) : productImageUrl ? (
                      <div
                        style={{
                          border: '1px solid #e1e3e5',
                          borderRadius: '8px',
                          padding: '8px',
                          backgroundColor: '#f4f6f8',
                        }}
                      >
                        <img
                          src={productImageUrl}
                          alt="Product"
                          style={{ maxHeight: '120px', borderRadius: '4px', objectFit: 'contain' }}
                        />
                      </div>
                    ) : (
                      <Banner tone="warning">
                        <p>No product image available from Shopify</p>
                      </Banner>
                    )}
                  </BlockStack>

                  {/* Generate Button */}
                  <Button
                    variant="primary"
                    onClick={async () => {
                      if (selectedLogoFileId && productImageUrl && selectedTemplateId) {
                        await aiMockup.generate(selectedLogoFileId, productImageUrl, selectedTemplateId);
                        billing.refetch();
                      }
                    }}
                    loading={aiMockup.generating}
                    disabled={
                      !selectedLogoFileId ||
                      !selectedTemplateId ||
                      !productImageUrl ||
                      aiMockup.generating ||
                      (billing.balance !== null && billing.balance.totalImageCapacity === 0)
                    }
                  >
                    Generate AI Mockup
                  </Button>

                  {/* Progress indicator */}
                  {aiMockup.generating && aiMockup.progress && (
                    <InlineStack gap="200" blockAlign="center">
                      <Spinner size="small" />
                      <Text as="span" variant="bodySm" tone="subdued">
                        {aiMockup.progress}
                      </Text>
                    </InlineStack>
                  )}

                  {/* Result Preview */}
                  {aiMockup.result && (
                    <BlockStack gap="200">
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        Generated Mockup
                      </Text>
                      <div
                        style={{
                          border: '2px solid #008060',
                          borderRadius: '8px',
                          padding: '8px',
                          backgroundColor: '#f1f8f5',
                        }}
                      >
                        <img
                          src={aiMockup.result.downloadUrl}
                          alt="Generated mockup"
                          style={{ maxWidth: '100%', borderRadius: '4px' }}
                        />
                      </div>
                      <InlineStack gap="200">
                        <Button
                          variant="primary"
                          onClick={async () => {
                            // Use the AI generated proof - it's already saved as a file
                            // Refetch to get the updated file list
                            await refetchDetail();
                            aiMockup.clearResult();
                          }}
                        >
                          Use as Proof
                        </Button>
                        <Button variant="plain" onClick={() => aiMockup.clearResult()}>
                          Discard
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  )}

                  {/* Error Banner */}
                  {aiMockup.error && (
                    <Banner
                      tone={aiMockup.billingError ? 'warning' : 'critical'}
                      onDismiss={() => aiMockup.clearError()}
                    >
                      <p>{aiMockup.error}</p>
                    </Banner>
                  )}
                </BlockStack>
              </Card>

              {/* Mockup Upload Card */}
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Upload Mockup
                  </Text>
                  <Divider />

                  {/* Existing proof files with selection checkboxes */}
                  {existingProofFiles.length > 0 && (
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Select proof files to include:
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {selectedProofFileIds.length} selected
                        </Text>
                      </InlineStack>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '12px',
                        }}
                      >
                        {existingProofFiles.map((file) => {
                          const displayUrl = getFileDisplayUrl(file);
                          const canDisplayImage = isImageFile(file.mimeType) && displayUrl;
                          const isSelected = selectedProofFileIds.includes(file.id);

                          // Check per-mockup approval status from previous rounds
                          const latestApproval = orderDetail?.approvals
                            ?.filter((a) => a.mockupApprovals && a.mockupApprovals.length > 0)
                            .sort((a, b) => b.version - a.version)[0];
                          const mockupApprovalStatus = latestApproval?.mockupApprovals?.find(
                            (ma) => ma.fileId === file.id
                          );

                          return (
                            <div
                              key={file.id}
                              onClick={() => {
                                setSelectedProofFileIds((prev) =>
                                  isSelected
                                    ? prev.filter((id) => id !== file.id)
                                    : [...prev, file.id]
                                );
                              }}
                              style={{
                                border: isSelected ? '2px solid #008060' : '1px solid #e1e3e5',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                position: 'relative',
                              }}
                            >
                              {/* Selection indicator */}
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '6px',
                                  left: '6px',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '4px',
                                  background: isSelected ? '#008060' : 'rgba(255,255,255,0.9)',
                                  border: isSelected ? 'none' : '2px solid #ccc',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 1,
                                  fontSize: '12px',
                                  color: '#fff',
                                  fontWeight: 700,
                                }}
                              >
                                {isSelected ? '✓' : ''}
                              </div>
                              {/* Per-mockup approval status badge */}
                              {mockupApprovalStatus && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '6px',
                                    right: '6px',
                                    zIndex: 1,
                                  }}
                                >
                                  <Badge
                                    tone={mockupApprovalStatus.status === 'approved' ? 'success' : mockupApprovalStatus.status === 'rejected' ? 'critical' : 'attention'}
                                    size="small"
                                  >
                                    {mockupApprovalStatus.status === 'approved' ? 'Approved' : mockupApprovalStatus.status === 'rejected' ? 'Rejected' : 'Needs Review'}
                                  </Badge>
                                </div>
                              )}
                              {/* Thumbnail */}
                              <div
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  backgroundColor: '#f4f6f8',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {canDisplayImage ? (
                                  <img
                                    src={displayUrl}
                                    alt={file.originalFilename}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                ) : (
                                  <Text as="span" variant="bodySm" tone="subdued">
                                    {file.mimeType.split('/')[1]?.toUpperCase() || 'FILE'}
                                  </Text>
                                )}
                              </div>
                              {/* File info */}
                              <div style={{ padding: '8px' }}>
                                <BlockStack gap="100">
                                  <Text as="span" variant="bodySm">
                                    {file.originalFilename.length > 20
                                      ? file.originalFilename.substring(0, 17) + '...'
                                      : file.originalFilename}
                                  </Text>
                                  {displayUrl && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Button
                                        variant="plain"
                                        size="slim"
                                        url={displayUrl}
                                        external
                                      >
                                        View Full Size
                                      </Button>
                                    </div>
                                  )}
                                </BlockStack>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </BlockStack>
                  )}

                  {/* Pending upload file previews */}
                  {mockupFiles.length > 0 && (
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Files to upload:
                        </Text>
                        <Button
                          variant="plain"
                          tone="critical"
                          onClick={() => setMockupFiles([])}
                        >
                          Remove all
                        </Button>
                      </InlineStack>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '12px',
                        }}
                      >
                        {mockupFiles.map((file, idx) => (
                          <div
                            key={`${file.name}-${idx}`}
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            {/* Remove button */}
                            <div
                              onClick={() => setMockupFiles((prev) => prev.filter((_, i) => i !== idx))}
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '12px',
                                lineHeight: 1,
                                zIndex: 1,
                              }}
                            >
                              &times;
                            </div>
                            {/* Thumbnail */}
                            <div
                              style={{
                                width: '100%',
                                height: '100px',
                                backgroundColor: '#f4f6f8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                  }}
                                />
                              ) : (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                                </Text>
                              )}
                            </div>
                            <div style={{ padding: '8px' }}>
                              <BlockStack gap="050">
                                <Text as="span" variant="bodySm">
                                  {file.name.length > 20
                                    ? file.name.substring(0, 17) + '...'
                                    : file.name}
                                </Text>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {formatFileSize(file.size)}
                                </Text>
                              </BlockStack>
                            </div>
                          </div>
                        ))}
                      </div>
                    </BlockStack>
                  )}

                  {/* DropZone for new files */}
                  <DropZone onDrop={handleFileDrop} accept="image/*,.pdf,.ai,.psd,.eps" allowMultiple>
                    <DropZone.FileUpload actionHint="Drop mockup files or click to upload" />
                  </DropZone>

                  {/* Notes field */}
                  <TextField
                    label="Notes for Customer"
                    value={proofNotes}
                    onChange={setProofNotes}
                    multiline={3}
                    autoComplete="off"
                    placeholder="Add any notes or instructions for the customer..."
                  />
                </BlockStack>
              </Card>

              {/* Send Button */}
              <Card>
                <InlineStack align="end" gap="200">
                  <Button
                    onClick={() => {
                      setSelectedOrderId(null);
                      setMockupFiles([]);
                      setProofNotes('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => setShowPortalPreview(true)}
                    disabled={!canSend}
                  >
                    Preview Portal
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSendForApproval}
                    loading={isSending}
                    disabled={!canSend || isSending}
                  >
                    Send for Approval
                  </Button>
                </InlineStack>
                {!canSend && (
                  <div style={{ marginTop: '8px' }}>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {isUndetermined
                        ? 'Set product type before sending for approval'
                        : !hasSizeProducts
                          ? 'Select size selection products before sending for approval'
                          : 'Select or upload proof files to send for approval'}
                    </Text>
                  </div>
                )}
              </Card>
            </BlockStack>
          )}
        </div>
      </div>

      {/* File Preview Modal */}
      <Modal
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        title={previewImage?.filename || ''}
        size="large"
      >
        <Modal.Section>
          {previewImage && (
            <BlockStack gap="300">
              <div style={{ textAlign: 'center' }}>
                <img
                  src={previewImage.url}
                  alt={previewImage.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <InlineStack align="center">
                <Button
                  onClick={() => {
                    // Find the file to determine download method
                    const file = referenceFiles.find(
                      (f) => getFileDisplayUrl(f) === previewImage.url
                    );
                    if (file?.externalUrl) {
                      window.open(file.externalUrl, '_blank');
                    } else if (file) {
                      actions.downloadFile(file.id, file.originalFilename);
                    }
                  }}
                >
                  Download Original
                </Button>
              </InlineStack>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* Logo-Mockup Mapping Modal */}
      <Modal
        open={showLogoMapping}
        onClose={() => {
          if (!isSending) setShowLogoMapping(false);
        }}
        title="Logo → Mockup Relationships"
        primaryAction={{
          content: 'Next: Preview Email',
          onAction: handleLogoMappingNext,
          loading: isSending,
        }}
        secondaryActions={[
          {
            content: 'Back to Edit',
            onAction: () => setShowLogoMapping(false),
            disabled: isSending,
          },
        ]}
        size="large"
      >
        <Modal.Section>
          <BlockStack gap="400">
            {/* "Use for all" quick select */}
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  Use for all mockups
                </Text>
                <InlineStack gap="300" blockAlign="end">
                  <div style={{ flex: 1 }}>
                    <Select
                      label=""
                      labelHidden
                      options={[
                        { label: 'Select a logo...', value: '' },
                        ...customerArtworkFiles.map((file) => ({
                          label: file.originalFilename,
                          value: file.id,
                        })),
                      ]}
                      value=""
                      onChange={(val) => {
                        if (val) {
                          const newMappings: Record<string, string> = {};
                          for (const id of selectedProofFileIds) {
                            newMappings[id] = val;
                          }
                          setLogoMappings(newMappings);
                        }
                      }}
                    />
                  </div>
                  <Text as="span" variant="bodySm" tone="subdued">or</Text>
                  <div style={{ minWidth: 200 }}>
                    <DropZone
                      onDrop={(files) => handleLogoUploadInMapping(files)}
                      accept="image/*,.pdf,.ai,.psd,.eps"
                      allowMultiple={false}
                      variableHeight
                    >
                      {logoUploading ? (
                        <div style={{ padding: '8px', textAlign: 'center' }}>
                          <Spinner size="small" />
                        </div>
                      ) : (
                        <div style={{ padding: '8px', textAlign: 'center' }}>
                          <Text as="span" variant="bodySm">Upload new logo</Text>
                        </div>
                      )}
                    </DropZone>
                  </div>
                </InlineStack>
              </BlockStack>
            </Card>

            <Divider />

            {/* Per-mockup logo mapping rows */}
            {selectedProofFileIds.map((mockupFileId) => {
              const mockupFile = existingProofFiles.find((f) => f.id === mockupFileId);
              const assignedLogoId = logoMappings[mockupFileId] || '';
              const assignedLogoFile = customerArtworkFiles.find((f) => f.id === assignedLogoId);
              const logoPreviewUrl = assignedLogoFile ? getFileDisplayUrl(assignedLogoFile) : null;
              const mockupPreviewUrl = mockupFile ? getFileDisplayUrl(mockupFile) : null;

              return (
                <Card key={mockupFileId}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Logo side (left) */}
                    <div style={{ flex: 1 }}>
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" fontWeight="semibold">Logo</Text>
                        <Select
                          label=""
                          labelHidden
                          options={[
                            { label: 'Select a logo...', value: '' },
                            ...customerArtworkFiles.map((file) => ({
                              label: file.originalFilename,
                              value: file.id,
                            })),
                          ]}
                          value={assignedLogoId}
                          onChange={(val) => {
                            setLogoMappings((prev) => ({ ...prev, [mockupFileId]: val }));
                          }}
                        />
                        {logoPreviewUrl ? (
                          <div
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              padding: '8px',
                              backgroundColor: '#f4f6f8',
                              textAlign: 'center',
                            }}
                          >
                            <img
                              src={logoPreviewUrl}
                              alt="Logo"
                              style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }}
                            />
                          </div>
                        ) : (
                          <DropZone
                            onDrop={(files) => handleLogoUploadInMapping(files, mockupFileId)}
                            accept="image/*,.pdf,.ai,.psd,.eps"
                            allowMultiple={false}
                            variableHeight
                          >
                            {logoUploading ? (
                              <div style={{ padding: '12px', textAlign: 'center' }}>
                                <Spinner size="small" />
                              </div>
                            ) : (
                              <div style={{ padding: '12px', textAlign: 'center' }}>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  Drop logo or click to upload
                                </Text>
                              </div>
                            )}
                          </DropZone>
                        )}
                      </BlockStack>
                    </div>

                    {/* Arrow */}
                    <div style={{ fontSize: '24px', color: '#8c9196', flexShrink: 0 }}>→</div>

                    {/* Mockup side (right) */}
                    <div style={{ flex: 1 }}>
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" fontWeight="semibold">Mockup</Text>
                        {mockupPreviewUrl ? (
                          <div
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              padding: '8px',
                              backgroundColor: '#f4f6f8',
                              textAlign: 'center',
                            }}
                          >
                            <img
                              src={mockupPreviewUrl}
                              alt="Mockup"
                              style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              border: '1px solid #e1e3e5',
                              borderRadius: '8px',
                              padding: '16px',
                              backgroundColor: '#f4f6f8',
                              textAlign: 'center',
                            }}
                          >
                            <Text as="span" variant="bodySm" tone="subdued">
                              {mockupFile?.originalFilename || 'Unknown file'}
                            </Text>
                          </div>
                        )}
                      </BlockStack>
                    </div>
                  </div>
                </Card>
              );
            })}

            {sendError && (
              <Banner tone="critical" onDismiss={() => setSendError(null)}>
                <p>{sendError}</p>
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Email Preview Modal */}
      <Modal
        open={showPreview}
        onClose={() => {
          if (!isConfirmSending) {
            setShowPreview(false);
          }
        }}
        title="Preview Approval Email"
        primaryAction={{
          content: 'Confirm & Send',
          onAction: handleConfirmSend,
          loading: isConfirmSending,
        }}
        secondaryActions={[
          {
            content: 'Back to Edit',
            onAction: () => setShowPreview(false),
            disabled: isConfirmSending,
          },
        ]}
        size="large"
      >
        <Modal.Section>
          {previewData && (
            <BlockStack gap="400">
              <InlineStack gap="200">
                <Text as="span" variant="bodyMd" fontWeight="semibold">To:</Text>
                <Text as="span" variant="bodyMd">{previewData.to}</Text>
              </InlineStack>
              <InlineStack gap="200">
                <Text as="span" variant="bodyMd" fontWeight="semibold">Subject:</Text>
                <Text as="span" variant="bodyMd">{previewData.subject}</Text>
              </InlineStack>
              <Divider />
              <iframe
                srcDoc={previewData.html}
                title="Email preview"
                style={{
                  width: '100%',
                  height: '500px',
                  border: '1px solid #e1e3e5',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                }}
                sandbox="allow-same-origin"
              />
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* Order Quick View Modal */}
      {selectedOrderId && (
        <Modal
          open={quickViewOpen}
          onClose={() => setQuickViewOpen(false)}
          title={orderDetail ? `${orderDetail.order.isDraft ? 'Draft ' : ''}Order ${orderDetail.order.shopifyOrderName}` : 'Order Details'}
          size="fullScreen"
          secondaryActions={[
            {
              content: 'Open Full Page',
              onAction: () => {
                navigate(`/orders/${selectedOrderId}`);
              },
            },
          ]}
        >
          <Modal.Section>
            <OrderDetailContent orderId={selectedOrderId} />
          </Modal.Section>
        </Modal>
      )}

      {/* Customer Portal Preview Modal */}
      {orderDetail && (
        <CustomerPortalPreview
          open={showPortalPreview}
          onClose={() => setShowPortalPreview(false)}
          order={orderDetail.order}
          proofImageUrls={portalPreviewImageUrls}
          proofNotes={proofNotes}
          versionHistory={approvalHistory}
        />
      )}

      {/* Loading overlay */}
      {isSending && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <BlockStack gap="200" inlineAlign="center">
            <Spinner size="large" />
            <Text as="p" variant="bodyMd">
              Sending for approval...
            </Text>
          </BlockStack>
        </div>
      )}
    </Page>
  );
}
